import React from "react";
import useAuth from "../hooks/useAuth.js";
import Navbar from "./Navbar.js";
import CallToAction from "./CallToAction.js";
import Footer from "./Footer.js";

function Terms() {
  const { isAuthenticated } = useAuth();

  return (
    <section id="Privacypagepage">
      <Navbar isAuthenticated={isAuthenticated} />

      <div className="container-fluid text-white  pl-16 py-5 shadow-sm page-header">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight">
              Terms and Conditions
            </h1>
            <div className="mt-4 flex justify-center gap-4">
              <p className="text-lg text-gray-100">
                Effective: <span className="font-medium">29/11/2025</span>
              </p>
              <p className="text-lg text-gray-100">
                Last Updated: <span className="font-medium">26/09/2026</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 md:p-6 lg:p-8">
        <h2 className="text-2xl font-bold mb-2">1. Definitions</h2>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-00 mb-2">
            "Ajo App" refers to the digital thrift contribution platform owned
            and operated by Craftr Studio Inc.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            "User" refers to any individual who registers or uses the Ajo App.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            "Thrift Group" refers to a collective of users contributing funds in
            a scheduled cycle.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            "Services" refers to the functionalities provided by the Ajo App,
            including group savings, contributions, and payouts.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">2. Eligibility</h2>
        <p className="text-sm text-gray-600 mb-4">To use the Ajo App:</p>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            You must be at least 18 years old.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            You must provide accurate and complete registration information.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            You must agree to comply with these Terms and applicable laws.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">3. User Responsibilities</h2>
        <p className="text-sm text-gray-600 mb-4">
          When using Ajo App, you agree to:
        </p>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            Provide truthful and current information.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Keep login credentials secure.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Meet contribution obligations in thrift groups.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Avoid fraud, misuse, or any form of platform disruption.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">
          4. Group Contributions and Payouts
        </h2>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            Contributions and payouts are executed based on the group schedule.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Payout orders are determined upon creation and cannot be altered.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Users failing to contribute may be removed or delayed in payouts.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">5. Payments and Payment-Related Services</h2>
        <p className="text-sm text-gray-600 mb-4">
          Some Ajo features enable payments, contributions, transfers, payouts, or payment requests. Available services, providers, payment methods, currencies, and fund flows may differ by feature, transaction, user location, eligibility, and provider availability. Ajo determines which options are available. Payment providers may require separate identity, business, or payout verification and their own terms apply.
        </p>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            Before confirming a transaction, users must review the recipient, purpose, amount, currency, and any applicable fee presented in the relevant Ajo flow or provider checkout.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            A browser redirect, screenshot, or user-submitted receipt is not by itself confirmation of payment. Where provider verification is available or required, Ajo may update payment status after receiving or confirming provider evidence.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Confirmation of a payment is separate from any subsequent payout, transfer, distribution, or settlement. A confirmed payment may remain subject to Ajo review, recipient verification, provider processing, or other controls before funds are released or delivered.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            A payment, payout, transfer, or settlement that is rejected, disputed, delayed, or flagged may be reviewed or investigated. That status does not mean a refund has been completed. Refund eligibility and processing depend on the transaction, applicable law, provider rules, and the relevant Ajo process; not every transaction can necessarily be reversed.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Ajo's role in a transaction depends on the feature and applicable provider arrangement. Ajo does not guarantee that funds will be held, released, transferred, or received by a particular date. Timing may depend on review, account verification, provider processing, banking networks, compliance requirements, or events outside Ajo's control.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Users must provide accurate transaction, identity, and payout information, use payment features only for lawful purposes, and comply with applicable provider terms. Provider verification, payment disputes, reversals, and processing decisions may be subject to separate provider terms and procedures. Nothing in these Terms excludes rights or remedies that cannot legally be excluded.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">6. Termination of Services</h2>
        <p className="text-sm text-gray-600 mb-4">
          Your account may be suspended or terminated if:
        </p>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            You breach these Terms.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Fraudulent or abusive behavior is detected.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            You miss scheduled financial obligations.
          </li>
        </ul>
        <p className="text-sm text-gray-600 mb-4">
          Users can close their accounts by emailing{" "}
          <a
            href="mailto:terminate@ajoapp.ca"
            className="text-blue-600 hover:text-blue-800"
          >
            terminate@ajoapp.ca
          </a>
          .
        </p>

        <h2 className="text-2xl font-bold mb-2">7. Limitation of Liability</h2>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            Ajo App is not responsible for financial outcomes or disputes between thrift group members.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            The platform is offered "as is," and we do not guarantee uninterrupted service.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Ajo App disclaims any liability for acts of fraud committed by users or errors from third-party service providers.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">8. Privacy Policy</h2>
        <p className="text-sm text-gray-600 mb-4">
          Use of Ajo App is also subject to our{" "}
          <a href="/privacy" className="text-blue-600 hover:text-blue-800">
            Privacy Policy
          </a>
          . Continued use of the platform confirms consent.
        </p>

        <h2 className="text-2xl font-bold mb-2">9. Intellectual Property</h2>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            All platform materials, designs, and functionalities belong to Craftr Studio Inc.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Unauthorized usage is prohibited.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">10. Modifications</h2>
        <p className="text-sm text-gray-600 mb-4">
          These Terms may be updated occasionally. Continued use indicates acceptance.
        </p>

        <h2 className="text-2xl font-bold mb-2">11. Dispute Resolution</h2>
        <ul className="list-disc pl-4 mb-4">
          <li className="text-sm text-gray-600 mb-2">
            Disputes must first attempt resolution through good-faith negotiation.
          </li>
          <li className="text-sm text-gray-600 mb-2">
            Legal issues are governed by Canadian law and subject to arbitration.
          </li>
        </ul>

        <h2 className="text-2xl font-bold mb-2">12. Contact</h2>
        <p className="text-sm text-gray-600 mb-4">
          If you have questions or concerns about these Terms, please contact
          us:
        </p>
        <p className="text-sm text-gray-600 mb-4">
          Email:{" "}
          <a
            href="mailto:info@ajoapp.ca"
            className="text-blue-600 hover:text-blue-800"
          >
            info@ajoapp.ca
          </a>
        </p>

        <p className="text-sm text-gray-600 mb-4">
          By using Ajo App, you acknowledge that you have read, understood, and
          agree to these Terms and Conditions.
        </p>
      </div>
      <CallToAction />
      <Footer />
    </section>
  );
}

export default Terms;